import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { ProtectedRoute } from '../../components/auth/index.js';
import { ErrorState } from '../../components/feedback/ErrorState';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton';
import { EvaluationSectionNav } from '../../components/features/project-feedback/EvaluationSectionNav.js';
import { FeedbackList } from '../../components/features/project-feedback/FeedbackList.js';
import { useProjectFeedbacks } from '../../hooks/useProjectFeedback.js';
import {
  filterFeedbacks,
  memberOptions,
  parseFeedbackListSearch,
  projectOptions,
  type FeedbackListSearch,
} from '../../lib/project-feedback-view.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business 評価者向けの案件 FB 一覧。API が返す権限内メンバーの FB を、メンバー・案件・異議ありで絞り込み、詳細で項目ごとに判断を付けに行く
 * @graph-connects useProjectFeedbacks [calls] 範囲内の FB 一覧
 * @graph-connects parseFeedbackListSearch [calls] 絞り込みの search param 検証
 */
export const Route = createFileRoute('/project-feedbacks/')({
  component: ProjectFeedbacksGuard,
  validateSearch: parseFeedbackListSearch,
});

/**
 * 他のメンバーの FB を並べるので、エンジニア評価の一覧と同じ権限を要求する（本人は
 * マイページから自分の FB を開く）。
 * @graph-connects ProtectedRoute [calls] 権限チェック
 */
function ProjectFeedbacksGuard() {
  return (
    <ProtectedRoute pageKey="engineerEvaluation">
      <ProjectFeedbacksPage />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function ProjectFeedbacksPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useProjectFeedbacks();
  const feedbacks = data?.feedbacks ?? [];
  const filtered = filterFeedbacks(feedbacks, {
    email: search.member,
    pjKey: search.pjKey,
    hasObjection: search.objection,
  });

  const updateSearch = (next: FeedbackListSearch) => {
    void navigate({ to: '/project-feedbacks', search: next, replace: true });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">案件 FB</h1>
        <EvaluationSectionNav current="feedback" />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Select
          aria-label="メンバー"
          size="sm"
          options={[{ value: '', label: '全メンバー' }, ...memberOptions(feedbacks)]}
          value={search.member ?? ''}
          onChange={(event) => {
            updateSearch({ ...search, member: event.target.value || undefined });
          }}
        />
        <Select
          aria-label="案件"
          size="sm"
          options={[{ value: '', label: '全案件' }, ...projectOptions(feedbacks)]}
          value={search.pjKey ?? ''}
          onChange={(event) => {
            updateSearch({ ...search, pjKey: event.target.value || undefined });
          }}
        />
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <Checkbox
            checked={search.objection === true}
            onChange={(event) => {
              updateSearch({ ...search, objection: event.target.checked || undefined });
            }}
          />
          異議あり
        </label>
      </div>

      {isLoading ? (
        <KpiTableSkeleton />
      ) : isError ? (
        <ErrorState
          onRetry={() => {
            void refetch();
          }}
        />
      ) : (
        <FeedbackList feedbacks={filtered} showMember emptyText="案件 FB はありません" />
      )}
    </div>
  );
}
